import { useState } from 'react';

import { ZapIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { BrainVersionSelector } from '@/components/atoms/BrainVersionSelector';
import {
  SegmentedChip,
  SegmentedChipLabel,
  SegmentedChipPlaceholder,
  SegmentedChipRemove,
  SegmentedChipSegment,
} from '@/components/atoms/SegmentedChip/SegmentedChip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Action, ActionType } from '@/shared/types/rules';

import { ActionEditor } from './ActionEditor';
import { ActionValueLabel } from './ActionValueLabel';
import { ACTION_ICONS, MENU_WRAPPER_CLASS } from './constants';
import {
  getActionLabel,
  hasActionValue,
  isActionComplete,
  needsActionValue,
  toAiAgentAction,
} from './utils';

type ActionChipProps = {
  action: Action;
  onChange: (action: Action) => void;
  onRemove: () => void;
  disabled?: boolean;
  showError?: boolean;
};

export const ActionChip = ({
  action,
  onChange,
  onRemove,
  disabled = false,
  showError = false,
}: ActionChipProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const label = getActionLabel(t, action.type);
  const valueLabel = hasActionValue(action) ? (
    <ActionValueLabel action={action} />
  ) : (
    <SegmentedChipPlaceholder>{t('rules.editor.set_value')}</SegmentedChipPlaceholder>
  );

  const renderValue = () => {
    if (disabled) {
      return (
        <SegmentedChipSegment variant="value" disabled>
          {valueLabel}
        </SegmentedChipSegment>
      );
    }

    if (action.type === ActionType.ASSIGN_BRAIN) {
      return (
        <BrainVersionSelector
          trigger={<SegmentedChipSegment variant="value">{valueLabel}</SegmentedChipSegment>}
          initialBrainId={action.brain_parent_id}
          initialVersion={action.brain_version}
          searchPlaceholder="rules.editor.search_ai_agent"
          onSelect={(selection) => onChange(toAiAgentAction(selection))}
        />
      );
    }

    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <SegmentedChipSegment variant="value">{valueLabel}</SegmentedChipSegment>
        </PopoverTrigger>
        <PopoverContent align="start" className={MENU_WRAPPER_CLASS}>
          <ActionEditor
            action={action}
            commitOnBlur
            onPick={(next) => {
              onChange(next);
              setIsOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
    );
  };

  return (
    <SegmentedChip error={showError && !isActionComplete(action)}>
      <SegmentedChipLabel icon={ACTION_ICONS[action.type] ?? ZapIcon}>{label}</SegmentedChipLabel>
      {needsActionValue(action.type) && renderValue()}
      {!disabled && <SegmentedChipRemove label={label} onClick={onRemove} />}
    </SegmentedChip>
  );
};
